import { InvoiceItem } from '../../config/types';

/**
 * The form is one long scrolling document; these are the anchors the step bar
 * jumps to and highlights as you scroll. Ids double as DOM element ids.
 */
export interface EntrySection {
  id: string;
  /** Short label for the step bar */
  step: string;
  /** Heading shown above the fields */
  title: string;
  /** Required fields in this section, used for the completion ring */
  required: Array<keyof InvoiceItem>;
}

export const ENTRY_SECTIONS: EntrySection[] = [
  {
    id: 'basic-information',
    step: 'Basic Info',
    title: 'Basic Information',
    required: [
      'rowNumber',
      'date',
      'orderNumber',
      'loadNumber',
      'invoiceNumber',
      'destination',
      'bolNumber',
      'terminal',
      'driver'
    ]
  },
  {
    id: 'amounts',
    step: 'Amounts',
    title: 'Amounts',
    required: ['gross', 'net', 'rate', 'fuelSurchargePercent']
  },
  {
    id: 'times',
    step: 'Times',
    title: 'Terminal & Delivery Times',
    required: [
      'terminalArrivalTime',
      'terminalDepartTime',
      'deliveryArrivalTime',
      'deliveryDepartTime'
    ]
  },
  {
    id: 'notes-summary',
    step: 'Notes',
    title: 'Notes & Summary',
    required: []
  },
  {
    id: 'invoice-information',
    step: 'Invoice Info',
    title: 'Invoice Information',
    required: []
  }
];

export const REQUIRED_FIELDS: Array<keyof InvoiceItem> = ENTRY_SECTIONS.flatMap(
  section => section.required
);

const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  return Boolean(value);
};

export const isFieldComplete = (formData: InvoiceItem, field: keyof InvoiceItem): boolean =>
  hasValue(formData[field]);

export interface CompletionState {
  completed: number;
  total: number;
  percent: number;
}

/** Drives the "Fields complete" ring in the workspace header. */
export const computeCompletion = (formData: InvoiceItem): CompletionState => {
  const completed = REQUIRED_FIELDS.filter(field => isFieldComplete(formData, field)).length;
  const total = REQUIRED_FIELDS.length;
  return {
    completed,
    total,
    percent: total === 0 ? 0 : Math.round((completed / total) * 100)
  };
};

/**
 * Which step the bar should highlight, given the section headings currently
 * inside the "current" band. The deepest one wins, so scrolling down advances
 * the step; when nothing is in the band the caller keeps its previous choice
 * (returns -1), which is what makes a long section hold its step.
 */
export const pickActiveSection = (visibleIds: Iterable<string>): number => {
  const visible = new Set(visibleIds);
  let active = -1;
  ENTRY_SECTIONS.forEach((section, index) => {
    if (visible.has(section.id)) active = index;
  });
  return active;
};
